/**
 * API client for the Vehicle Inventory ledger (source of truth).
 * Mirrors backend/schemas.py InventoryRecordResponse / InventoryListResponse /
 * InventoryFreshnessResponse and the /inventory/* endpoints.
 */
import { API } from "../config/api"

// --- Types matching backend Pydantic schemas ---

// One RapidRecon source for a car (a per-store file or the DOLANRECON catch-all).
// The detail modal renders every source, since the store and recon center often
// report different steps for the same car.
export interface ReconMatch {
  source: string | null
  step: string | null
  notes: string | null
}

// One HomeNet listing's photo count for a car (its franchise store row or its Lee
// Brothers wholesale row). The detail modal shows both when a car is dual-listed.
export interface PhotoMatch {
  source: string | null
  photos: number | null
}

export interface InventoryRecord {
  id: number
  vin: string | null
  stock_number: string | null
  rooftop: string | null
  presence_status: string | null
  tekion_status: string | null
  vehicle_type: string | null
  marketable: boolean | null
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  body_style: string | null
  odometer: number | null
  color_ext: string | null
  color_int: string | null
  color_ext_code: string | null
  color_int_code: string | null
  drive_line: string | null
  cylinders: number | null
  fuel_type: string | null
  model_code: string | null
  vehicle_location: string | null
  title_status: string | null
  title_status_notes: string | null
  age_days: number | null
  creation_date: string | null
  stocked_in_date: string | null
  received_date: string | null
  sold_date: string | null
  in_service_date: string | null
  last_modified_date: string | null
  price_msrp: number | null
  price_retail: number | null
  price_internet: number | null
  price_invoice: number | null
  price_wholesale: number | null
  price_asking: number | null
  price_acv: number | null
  valuation_mmr: number | null
  times_stocked: number | null
  // Enrichment (HomeNet / RapidRecon / vAuto)
  photos_count: number | null
  website_status: string | null
  photo_matches: PhotoMatch[] | null // both HomeNet counts (franchise + Lee Bros), for the modal
  marketed_at_lee_bros: boolean | null // listed at the Lee Brothers wholesale store
  // Live website (Dealer.com scrape), matched by VIN to the active record
  website_live: boolean | null
  website_url: string | null
  website_price: number | null
  website_photos_count: number | null
  website_carfax_url: string | null
  // The certified check: HomeNet's Dealer Cert and the listing's certified sticker
  hn_dealer_cert: boolean | null
  website_cert_sticker: boolean | null
  website_cert_program: string | null
  recon_step: string | null
  recon_notes: string | null
  recon_matches: ReconMatch[] | null // every recon source, for the detail modal
  valuation_kbb: number | null
  kbb_trade_in: number | null
  unit_cost: number | null
  recon_cost: number | null
  pack_cost: number | null
  other_cost: number | null
  transport_cost: number | null
  wholesale_status: string | null
  appraiser: string | null
  vehicle_source: string | null
  vauto_notes: string | null
}

export interface InventoryListResponse {
  records: InventoryRecord[]
  total: number
  page: number
  size: number
}

export interface InventoryFreshnessResponse {
  last_updated_at: string | null
  records_total: number
}

export interface ListInventoryParams {
  dealerGroupId?: number | null
  page?: number
  size?: number
  sort?: string
  search?: string
  rooftop?: string[]
  status?: string[]
  vehicleType?: string[]
  website?: string[]
  yearMin?: number | null
  yearMax?: number | null
  ageMin?: number | null
  ageMax?: number | null
  odometerMin?: number | null
  odometerMax?: number | null
}

export interface InventoryFilterOptions {
  rooftops: string[]
  statuses: string[]
  vehicle_types: string[]
  website_presence: string[]
  year: { min: number | null; max: number | null }
  age_days: { min: number | null; max: number | null }
  odometer: { min: number | null; max: number | null }
}

// Mirrors backend schemas.DealerNotesResponse: the "Notes from the dealer" text last
// read from the car's page on the group's site. read_at is null until it's been read.
export interface DealerNotes {
  vin: string
  notes: string | null
  certified_phrases: string[]
  read_at: string | null
  vdp_url: string | null
}

// --- Auth helper (local copy matching existing pattern across api files) ---

const TOKEN_KEY = "invi_token"

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY)
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
  return res.json()
}

// --- Fetchers ---

export async function listInventoryRecords(
  params: ListInventoryParams,
): Promise<InventoryListResponse> {
  const query = new URLSearchParams()
  if (params.dealerGroupId != null) query.set("dealer_group_id", String(params.dealerGroupId))
  if (params.page != null) query.set("page", String(params.page))
  if (params.size != null) query.set("size", String(params.size))
  if (params.sort) query.set("sort", params.sort)
  if (params.search) query.set("search", params.search)
  params.rooftop?.forEach((value) => query.append("rooftop", value))
  params.status?.forEach((value) => query.append("status", value))
  params.vehicleType?.forEach((value) => query.append("vehicle_type", value))
  params.website?.forEach((value) => query.append("website_presence", value))
  if (params.yearMin != null) query.set("year_min", String(params.yearMin))
  if (params.yearMax != null) query.set("year_max", String(params.yearMax))
  if (params.ageMin != null) query.set("age_min", String(params.ageMin))
  if (params.ageMax != null) query.set("age_max", String(params.ageMax))
  if (params.odometerMin != null) query.set("odometer_min", String(params.odometerMin))
  if (params.odometerMax != null) query.set("odometer_max", String(params.odometerMax))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.records}?${query.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<InventoryListResponse>(res)
}

export async function getDealerNotes(
  vin: string,
  dealerGroupId?: number | null,
): Promise<DealerNotes> {
  const query = new URLSearchParams({ vin })
  if (dealerGroupId != null) query.set("dealer_group_id", String(dealerGroupId))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.dealerNotes}?${query.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<DealerNotes>(res)
}

export async function getInventoryFilterOptions(
  dealerGroupId?: number | null,
): Promise<InventoryFilterOptions> {
  const groupParam = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.filterOptions}${groupParam}`,
    { headers: authHeaders() },
  )
  return handleResponse<InventoryFilterOptions>(res)
}

export async function getInventoryFreshness(
  dealerGroupId?: number | null,
): Promise<InventoryFreshnessResponse> {
  const groupParam = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.freshness}${groupParam}`,
    { headers: authHeaders() },
  )
  return handleResponse<InventoryFreshnessResponse>(res)
}

export interface InventoryDigestStatus {
  id: number | null
  status: string | null // running | success | failed | null (never run)
  stage: string | null // live progress label while running
  started_at: string | null
  completed_at: string | null
  records_new: number | null
  records_updated: number | null
  changes_logged: number | null
  anomalies_count: number | null
  error_message: string | null
}

export async function uploadInventoryFile(
  files: File[],
  dealerGroupId?: number | null,
): Promise<{ status: string }> {
  const form = new FormData()
  files.forEach((file) => form.append("files", file))
  const groupParam = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const token = localStorage.getItem(TOKEN_KEY)
  // No Content-Type header — the browser sets the multipart boundary itself.
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.upload}${groupParam}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  return handleResponse<{ status: string }>(res)
}

export async function getInventoryDigestStatus(
  dealerGroupId?: number | null,
): Promise<InventoryDigestStatus> {
  const groupParam = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.digestStatus}${groupParam}`,
    { headers: authHeaders() },
  )
  return handleResponse<InventoryDigestStatus>(res)
}

// --- Update Inventory staging (the /inventory/update page) ---

export interface StagedFile {
  slot: string
  label: string
  filename: string
  row_count: number | null
  uploaded_by: string | null
  uploaded_at: string | null
}

export interface InventoryUpdateSlot {
  slot: string
  label: string
  required: boolean
  hint: string
  present: boolean
}

export interface InventoryUpdateStatus {
  slots: InventoryUpdateSlot[]
  can_run: boolean
  mode: string // "seed" | "update"
  warnings: string[]
  stale_slots: string[]
}

export interface InventoryRunHistoryEntry {
  id: number
  started_at: string | null
  completed_at: string | null
  status: string
  triggered_by: string | null
  records_new: number | null
  records_updated: number | null
  changes_logged: number | null
  error_message: string | null
}

export interface StageFilesResult {
  staged: StagedFile[]
  rejected: { filename: string; reason: string }[]
}

const groupQ = (dealerGroupId?: number | null) =>
  dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""

export async function stageInventoryFiles(files: File[], dealerGroupId?: number | null): Promise<StageFilesResult> {
  const form = new FormData()
  files.forEach((file) => form.append("files", file))
  const token = localStorage.getItem(TOKEN_KEY)
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateFiles}${groupQ(dealerGroupId)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  return handleResponse<StageFilesResult>(res)
}

export async function listStagedInventoryFiles(dealerGroupId?: number | null): Promise<StagedFile[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateFiles}${groupQ(dealerGroupId)}`, {
    headers: authHeaders(),
  })
  return handleResponse<StagedFile[]>(res)
}

export async function getInventoryUpdateStatus(dealerGroupId?: number | null): Promise<InventoryUpdateStatus> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateStatus}${groupQ(dealerGroupId)}`, {
    headers: authHeaders(),
  })
  return handleResponse<InventoryUpdateStatus>(res)
}

export async function deleteStagedInventoryFile(slot: string, dealerGroupId?: number | null): Promise<void> {
  const query = new URLSearchParams({ slot })
  if (dealerGroupId != null) query.set("dealer_group_id", String(dealerGroupId))
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateFiles}?${query.toString()}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  await handleResponse<{ status: string }>(res)
}

export async function clearStagedInventoryFiles(dealerGroupId?: number | null): Promise<void> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateClear}${groupQ(dealerGroupId)}`, {
    method: "POST",
    headers: authHeaders(),
  })
  await handleResponse<{ status: string }>(res)
}

export async function runInventoryUpdate(dealerGroupId?: number | null): Promise<void> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateRun}${groupQ(dealerGroupId)}`, {
    method: "POST",
    headers: authHeaders(),
  })
  await handleResponse<{ status: string }>(res)
}

export async function getInventoryUpdateHistory(dealerGroupId?: number | null): Promise<InventoryRunHistoryEntry[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.inventory.updateHistory}${groupQ(dealerGroupId)}`, {
    headers: authHeaders(),
  })
  return handleResponse<InventoryRunHistoryEntry[]>(res)
}

export interface InventoryChange {
  id: number
  vin: string | null
  stock_number: string | null
  change_type: string // arrival | transfer | unwind | sold | field_change | cleared
  field: string | null
  old_value: string | null
  new_value: string | null
  detected_at: string | null
  digest_run_id: number | null
}

export interface InventoryChangeListResponse {
  changes: InventoryChange[]
  total: number
  page: number
  size: number
}

export interface ListChangesParams {
  dealerGroupId?: number | null
  page?: number
  size?: number
  changeType?: string
  search?: string
}

export async function listInventoryChanges(
  params: ListChangesParams,
): Promise<InventoryChangeListResponse> {
  const query = new URLSearchParams()
  if (params.dealerGroupId != null) query.set("dealer_group_id", String(params.dealerGroupId))
  if (params.page != null) query.set("page", String(params.page))
  if (params.size != null) query.set("size", String(params.size))
  if (params.changeType) query.set("change_type", params.changeType)
  if (params.search) query.set("search", params.search)
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.changes}?${query.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<InventoryChangeListResponse>(res)
}

// --- Vehicle history (the full-details page) ---

export interface VehicleStockingRef {
  stock_number: string | null
  rooftop: string | null
  tekion_status: string | null
}

export interface VehicleHistory {
  record: InventoryRecord
  changes: InventoryChange[]
  other_stockings: VehicleStockingRef[]
}

export async function getVehicleHistory(
  vin: string,
  stock: string,
  dealerGroupId?: number | null,
): Promise<VehicleHistory> {
  const query = new URLSearchParams({ vin, stock })
  if (dealerGroupId != null) query.set("dealer_group_id", String(dealerGroupId))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.inventory.vehicleHistory}?${query.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<VehicleHistory>(res)
}
